<template>
	<v-data-table
		class="rounded-lg bordered new-back lighten2 billables-table"
		checkbox-color="primary"
		:headers="shownHeaders"
		:items="billables"
		:items-per-page.sync="pagination.itemsPerPage"
		:page.sync="pagination.page"
		:sort-by.sync="pagination.sortBy"
		:sort-desc.sync="pagination.sortDesc"
		@update:page="onPageChange"
		@update:items-per-page="onItemsPerPageChange"
		@update:sort-by="onSortByChange"
		@update:sort-desc="onSortDescChange"
		:server-items-length="pagination.totalItems"
		v-bind="$attrs" v-on="$listeners"
	>
		<template #[`item.code`]="{ item }">
			<div class="text-left text-truncate code-cell" :title="item.code">
				{{ item.code }}
			</div>
		</template>
		<template #[assignedHeaderSlot]>
			<v-tooltip bottom z-index="300">
				<template #activator="{ on, attrs }">
					<span
						v-bind="attrs"
						v-on="on"
						class="header-tooltip-trigger d-inline-flex justify-end align-center w-100"
					>
						Assigned
						<v-icon small class="header-info-icon ml-1">mdi-information-outline</v-icon>
					</span>
				</template>
				<span>Sum of Billable Assigned Hours</span>
			</v-tooltip>
		</template>
		<template #[executedHeaderSlot]>
			<v-tooltip bottom z-index="300">
				<template #activator="{ on, attrs }">
					<span
						v-bind="attrs"
						v-on="on"
						class="header-tooltip-trigger d-inline-flex justify-end align-center w-100"
					>
						Executed
						<v-icon small class="header-info-icon ml-1">mdi-information-outline</v-icon>
					</span>
				</template>
				<span>Sum of Billable Executed Hours</span>
			</v-tooltip>
		</template>
		<template #[`item.customerName`]="{ item }">
			<div class="text-left">
				{{ item.customerName }}
			</div>
		</template>
		<template #[`item.projects`]="{ item }">
			<div class="project-col d-flex align-center">
				<template v-if="projectList(item).length">
					<div
						class="project"
						:class="{ 'project--compact': projectList(item).length > 1 }"
						:style="{ background: projectStyle(projectList(item)[0]).lightColor }"
					>
						<div
							class="letter"
							:style="{ background: projectStyle(projectList(item)[0]).color }"
						>
							{{ projectStyle(projectList(item)[0]).small }}
						</div>
						<div class="name">
							{{ truncateProjectName(projectList(item)[0].name, projectList(item).length > 1) }}
						</div>
					</div>
					<v-tooltip v-if="projectList(item).length > 1" bottom max-width="320" z-index="300">
						<template #activator="{ on, attrs }">
							<span
								v-bind="attrs"
								v-on="on"
								class="project-more-badge ml-1 cursor-pointer"
							>
								+{{ projectList(item).length - 1 }}
							</span>
						</template>
						<div class="projects-tooltip pa-2">
							<div class="caption font-weight-bold mb-2">Projects</div>
							<div
								v-for="project in projectList(item)"
								:key="project.id"
								class="project project--tooltip mb-1"
								:style="{ background: projectStyle(project).lightColor }"
							>
								<div
									class="letter"
									:style="{ background: projectStyle(project).color }"
								>
									{{ projectStyle(project).small }}
								</div>
								<div class="name">{{ project.name }}</div>
							</div>
						</div>
					</v-tooltip>
				</template>
				<span v-else class="grey--text">—</span>
			</div>
		</template>
		<template #[`item.currency`]="{ item }">
			<v-avatar size="24" class="mr-1">
				<v-img :src="require(`../assets/currencies/${(item.currency || 'USD').toLowerCase()}.png`)" />
			</v-avatar>
			<b>{{ item.currency }}</b>
		</template>
		<template #[`item.type`]="{ item }">
			{{ codeByType[item.type] }}
		</template>
		<template #[`item.startDate`]="{ item }">
			{{ item.startDate | formatDate }}
		</template>
		<template #[`item.status`]="{ item }">
			<v-menu offset-x right open-on-hover>
				<template #activator="{ on }">
					<v-btn
						v-on="on"
						:color="item.statusColor"
						outlined
						small
						class="status-pill rounded-xl"
						:style="{
							background: $vuetify.theme.isDark ? '' : item.statusLightColor,
						}"
					>
						{{ statusLabel(item) }}
					</v-btn>
				</template>

				<v-list dense>
					<v-list-item
						v-for="approver in (item.approvers || []).filter(x => x.statusToApprove === 1)"
						:key="approver.id"
					>
						<div
							class="d-flex justify-space-between align-center"
							style="min-width: 20px; width: 100%"
						>
							<span class="mr-2 body-2">
								<user-avatar :user-id="approver.approverId" :size="28" :show-card="false" />
								{{ approver.approverBlueTag }}
							</span>
							<span :set="status = approverStatus.find(s => s.id == approver.status)">
								<v-chip
									:color="status.lightColor"
									:style="{ color: status.color }"
									small
								>
									{{ status.description }}
								</v-chip>
							</span>
						</div>
					</v-list-item>
				</v-list>
			</v-menu>
		</template>
		<template #[`item.contracted`]="{ item }">
			<div class="metric-col text-end">
				<template v-if="item.isOpen">
					<div class="metric-main">—</div>
					<div class="metric-sub text--secondary">Open</div>
				</template>
				<template v-else>
					<div class="metric-main font-weight-bold">{{ formatMoney(item.subtotalWithDiscount) }}</div>
					<div v-if="item.contractedHours > 0" class="metric-sub text--secondary">
						{{ formatHours(item.contractedHours) }} hrs
					</div>
				</template>
			</div>
		</template>
		<template #[`item.assigned`]="{ item }">
			<div v-if="!hoursMetricsApply(item)" class="text-end">—</div>
			<div v-else class="metric-col text-end" :class="assignedTrafficClass(item)">
				<div class="metric-main font-weight-bold assigned-pct">
					{{ assignedPct(item) }}
				</div>
				<div class="metric-sub assigned-hrs">{{ formatHours(assignedHours(item)) }} hrs</div>
			</div>
		</template>
		<template #[`item.executed`]="{ item }">
			<div v-if="!hoursMetricsApply(item)" class="text-end	">—</div>
			<div v-else class="metric-col text-end">
				<div class="metric-main font-weight-bold executed-pct">
					{{ executedPct(item) }}
				</div>
				<div class="metric-sub executed-hrs">{{ formatHours(executedHours(item)) }} hrs</div>
			</div>
		</template>
		<template #[`item.billed`]="{ item }">
			<div v-if="item.isOpen || !item.subtotalWithDiscount" class="text-end">—</div>
			<div v-else class="metric-col text-end">
				<div class="metric-main font-weight-bold billed-pct">
					{{ billedPct(item) }}
				</div>
				<div class="metric-sub billed-amt">{{ formatMoney(netBilled(item), item.currency) }}</div>
			</div>
		</template>
		<template #[`item.actions`]="{ item }">
			<v-menu v-if="$security.can('edit')" left offset-x>
				<template #activator="{ on }">
					<v-btn v-on="on" icon><v-icon>mdi-dots-vertical</v-icon></v-btn>
				</template>
				<v-list dense class="py-1">
					<v-list-item v-if="item.canApprove" class="pa-1 py-0">
						<v-tooltip right z-index="200">
							<template #activator="{ on }">
								<v-btn v-on="on" @click="$emit('validate', item)" color="primary" text small>
									<v-icon>mdi-check</v-icon>
								</v-btn>
							</template>
							<span class="caption">Validate</span>
						</v-tooltip>
					</v-list-item>
					<v-list-item class="pa-1 py-0">
						<v-tooltip right z-index="200">
							<template #activator="{ on }">
								<v-btn v-on="on" @click="$adminSurf('openHistory', item, 'billable')" text small>
									<v-icon>mdi-history</v-icon>
								</v-btn>
							</template>
							<span class="caption">History</span>
						</v-tooltip>
					</v-list-item>
					<v-list-item class="pa-1 py-0">
						<v-tooltip right z-index="200">
							<template #activator="{ on }">
								<v-btn v-on="on" @click="$emit('duplicate', item.id)" text small color="grey">
									<v-icon>mdi-content-copy</v-icon>
								</v-btn>
							</template>
							<span class="caption">Duplicate</span>
						</v-tooltip>
					</v-list-item>
					<v-list-item v-if="!item.isCancelled && item.status != 6" class="pa-1 py-0">
						<v-tooltip right z-index="200">
							<template #activator="{ on }">
								<v-btn v-on="on" @click="$adminSurf('closeBillable', item)" text small color="secondary">
									<v-icon>mdi-door</v-icon>
								</v-btn>
							</template>
							<span class="caption">Close</span>
						</v-tooltip>
					</v-list-item>
					<v-list-item v-if="!item.isCancelled && item.status == 6 && $security.can('full_access')" class="pa-1 py-0">
						<v-tooltip right z-index="200">
							<template #activator="{ on }">
								<v-btn v-on="on" @click="$adminSurf('openBillable', item)" text small color="secondary">
									<v-icon>mdi-door-open</v-icon>
								</v-btn>
							</template>
							<span class="caption">Reopen</span>
						</v-tooltip>
					</v-list-item>
					<v-list-item v-if="!item.isCancelled && item.status != 6" class="pa-1 py-0">
						<v-tooltip right z-index="200">
							<template #activator="{ on }">
								<v-btn v-on="on" @click="$emit('cancel', item)" text small color="error">
									<v-icon>mdi-cancel</v-icon>
								</v-btn>
							</template>
							<span class="caption">Cancel</span>
						</v-tooltip>
					</v-list-item>
					<v-list-item class="pa-1 py-0">
						<watchers-menu
							@watcher-added="item.subscribers.push($event)"
							@watcher-removed="item.subscribers.splice(item.subscribers.findIndex(x => x.userId == $event), 1)"
							:entity-id="item.id"
							:watchers="item.subscribers"
							required-permission="billables"
						/>
					</v-list-item>
				</v-list>
			</v-menu>
		</template>
	</v-data-table>
</template>

<script>
import BillableJSON from '../json/billable.json'
import WatchersMenu from './admin-surf/WatchersMenu'

export default {
	props: {
		billables: Array,
		small: Boolean,
		xSmall: Boolean,
		pagination: {
			type: Object,
			default: () => ({
				page: 1,
				itemsPerPage: 10,
			}),
		},
	},
	components: { WatchersMenu },
	data: () => ({
		tableReady: false,
		headers: [
			{
				text: 'Code',
				value: 'code',
				small: true,
				xSmall: true,
				width: '150px',
				cellClass: 'code-col-cell',
			},
			{ text: 'Customer', value: 'customerName', small: true },
			{
				text: 'Project',
				value: 'projects',
				align: 'start',
				small: true,
				xSmall: true,
				sortable: false,
				width: '200px',
				cellClass: 'project-col-cell',
			},
			{ text: 'Start Date', value: 'startDate' },
			{ text: 'Type', value: 'type', align: 'center' },
			{
				text: 'Status',
				value: 'status',
				align: 'center',
				small: true,
				sortable: false,
				width: '128px',
				cellClass: 'status-col-cell',
			},
			{ text: 'Currency', value: 'currency', align: 'center' },
			{ text: 'Contracted', value: 'contracted', align: 'end', width: '130px', sortable: false, small: true },
			{ text: 'Assigned', value: 'assigned', align: 'end', width: '120px', sortable: false, small: true },
			{ text: 'Executed', value: 'executed', align: 'end', width: '120px', sortable: false, small: true },
			{ text: 'Billed', value: 'billed', align: 'end', width: '120px', sortable: false, small: true },
			{ text: '', value: 'actions', width: '1', small: false, sortable: false },
		],
		codeByType: ['FxR', 'RPAH', 'RPEH', 'Int'],
		projectTypes: [],
		approverStatus: [],
		defaultProjectType: { color: '#757575', lightColor: '#eeeeee', small: '?' },
	}),
	mounted() {
		this.projectTypes = BillableJSON.projectTypes
		this.$http
			.get('api/enums/approver/status')
			.then(res => {
				this.approverStatus = res.data
			})
			.catch(err => {
				console.error(err)
				this.$root.error('Cannot obtain billable items')
			})
		this.$nextTick(() => {
			this.tableReady = true
		})
	},
	computed: {
		shownHeaders() {
			if (this.xSmall) return this.headers.filter(h => h.xSmall)
			return this.headers.filter(h => !this.small || h.small)
		},
		assignedHeaderSlot() {
			return 'header.assigned'
		},
		executedHeaderSlot() {
			return 'header.executed'
		},
	},
	methods: {
		emitPagination() {
			if (!this.tableReady) return
			this.$emit('pagination')
		},
		onPageChange() {
			this.emitPagination()
		},
		onItemsPerPageChange() {
			this.emitPagination()
		},
		onSortByChange() {
			this.emitPagination()
		},
		onSortDescChange() {
			this.emitPagination()
		},
		projectList(item) {
			return item.customerProjects || []
		},
		/** Hasta 14 caracteres del nombre; si hay más, + "...". Con +N, menos caracteres para ancho fijo. */
		truncateProjectName(name, compact) {
			const n = (name || '').trim()
			if (compact) {
				const max = 7
				if (n.length <= max) return n
				return `${n.slice(0, max)}...`
			}
			if (n.length <= 14) return n
			return `${n.slice(0, 14)}...`
		},
		projectStyle(project) {
			const t = this.projectTypes.find(pt => pt.value == project.type)
			return t || this.defaultProjectType
		},
		statusLabel(item) {
			const name = (item.statusDisplayName || '').trim()
			if (/^to be assigned$/i.test(name)) return 'Not Assigned'
			return name
		},
		formatHours(n) {
			return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n || 0)
		},
		formatMoney(amount) {
			const cur = 'USD'
			try {
				return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(amount || 0)
			} catch {
				return `$${(amount || 0).toFixed(2)}`
			}
		},
		formatPct(num, den) {
			if (!den || den <= 0) return '—'
			const p = (num / den) * 100
			return `${Math.round(p)}%`
		},
		hoursMetricsApply(item) {
			return item.type !== 0 && item.type !== 2
		},
		assignedHours(item) {
			return (item.plannedBillableHours || 0)
		},
		executedHours(item) {
			return (item.executedBillableHours || 0)
		},
		hourDenominator(item) {
			if (item.isOpen) return 0
			const ch = item.contractedHours || 0
			if (ch > 0) return ch
			return 0
		},
		assignedPct(item) {
			const den = this.hourDenominator(item)
			if (den > 0) return this.formatPct(this.assignedHours(item), den)
			const h = this.assignedHours(item)
			return h > 0 ? '—' : '0%'
		},
		/** Verde ≥98%, ámbar ≥75% y <98%, rojo <75% (sobre horas contratadas). */
		assignedTrafficClass(item) {
			const den = this.hourDenominator(item)
			const hrs = this.assignedHours(item)
			if (den > 0) {
				const p = (hrs / den) * 100
				if (p >= 98) return 'assigned-traffic assigned-traffic--good'
				if (p >= 75) return 'assigned-traffic assigned-traffic--warn'
				return 'assigned-traffic assigned-traffic--bad'
			}
			if (hrs > 0) return 'assigned-traffic assigned-traffic--muted'
			return 'assigned-traffic assigned-traffic--bad'
		},
		executedPct(item) {
			const den = this.hourDenominator(item)
			if (den > 0) return this.formatPct(this.executedHours(item), den)
			const h = this.executedHours(item)
			return h > 0 ? '—' : '0%'
		},
		netBilled(item) {
			return (item.billed || 0) + (item.positiveAdjustment || 0) + (item.negativeAdjustment || 0)
		},
		billedPct(item) {
			const sub = item.subtotalWithDiscount || 0
			if (sub <= 0) return '—'
			return this.formatPct(this.netBilled(item), sub)
		},
	},
}
</script>

<style lang="scss" scoped>
.billables-table ::v-deep th.code-col-cell,
.billables-table ::v-deep td.code-col-cell {
	width: 150px;
	max-width: 150px;
	min-width: 150px;
	vertical-align: middle;
}
.code-cell {
	max-width: 100%;
	overflow: hidden;
}
.billables-table ::v-deep th.status-col-cell,
.billables-table ::v-deep td.status-col-cell {
	width: 128px;
	max-width: 128px;
	min-width: 128px;
	padding-left: 6px !important;
	padding-right: 6px !important;
	vertical-align: middle;
}
.billables-table ::v-deep .status-pill {
	width: 100%;
	max-width: 116px;
	min-width: 116px;
	justify-content: center;
	text-transform: none;
	letter-spacing: normal;
	font-size: 11px;
	margin: 0 auto;
	padding-left: 6px !important;
	padding-right: 6px !important;
}
.billables-table ::v-deep .status-pill .v-btn__content {
	padding: 0 2px;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.header-tooltip-trigger {
	cursor: help;
	user-select: none;
}
.header-info-icon {
	opacity: 0.65;
	font-size: 16px !important;
}
.header-tooltip-trigger:hover .header-info-icon {
	opacity: 1;
}
.metric-col {
	line-height: 1.25;
}
.assigned-traffic .metric-main,
.assigned-traffic .metric-sub {
	color: inherit;
}
.assigned-traffic--good .metric-main,
.assigned-traffic--good .metric-sub {
	color: #2e7d32 !important;
}
.theme--dark .assigned-traffic--good .metric-main,
.theme--dark .assigned-traffic--good .metric-sub {
	color: #81c784 !important;
}
.assigned-traffic--warn .metric-main,
.assigned-traffic--warn .metric-sub {
	color: #f57f17 !important;
}
.theme--dark .assigned-traffic--warn .metric-main,
.theme--dark .assigned-traffic--warn .metric-sub {
	color: #ffca28 !important;
}
.assigned-traffic--bad .metric-main,
.assigned-traffic--bad .metric-sub {
	color: #c62828 !important;
}
.theme--dark .assigned-traffic--bad .metric-main,
.theme--dark .assigned-traffic--bad .metric-sub {
	color: #ef5350 !important;
}
.assigned-traffic--muted .metric-main,
.assigned-traffic--muted .metric-sub {
	color: rgba(0, 0, 0, 0.54) !important;
}
.theme--dark .assigned-traffic--muted .metric-main,
.theme--dark .assigned-traffic--muted .metric-sub {
	color: rgba(255, 255, 255, 0.5) !important;
}
.metric-main {
	font-size: 14px;
}
.metric-sub {
	font-size: 12px;
	margin-top: 2px;
}
.cursor-pointer {
	cursor: pointer;
}
.project {
	display: inline-flex;
	align-items: center;
	border-radius: 15px;
	margin-right: 5px;
	white-space: nowrap;
	height: 30px;
	div {
		display: inline-flex;
		align-items: center;
		justify-content: center;
	}
	.letter {
		width: 30px;
		padding: 5px;
		color: white;
		border-radius: 15px;
		flex-shrink: 0;
	}
	.name {
		padding: 0 10px 0 5px;
		color: black;
	}
}
.project--tooltip {
	display: flex;
	width: 100%;
}
.projects-tooltip {
	max-height: 280px;
	overflow-y: auto;
}
.billables-table ::v-deep th.project-col-cell,
.billables-table ::v-deep td.project-col-cell {
	width: 200px;
	max-width: 200px;
	min-width: 200px;
	vertical-align: middle;
}
.project-col {
	width: 100%;
	max-width: 200px;
	min-width: 200px;
	overflow: hidden;
	flex-wrap: nowrap;
}
.project--compact .name {
	max-width: 72px;
	padding-right: 6px;
	overflow: hidden;
}
.project:not(.project--compact) .name {
	max-width: 132px;
	padding-right: 8px;
	overflow: hidden;
}
.project-more-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 26px;
	height: 26px;
	padding: 0 4px;
	border-radius: 50%;
	background: var(--v-primary-base);
	color: #fff !important;
	font-size: 11px;
	font-weight: 700;
	flex-shrink: 0;
	line-height: 1;
}
</style>
